import FindRecipientResult from './FindRecipientResult';
import addHighlightedFindRecipient from '../actions/addHighlightedFindRecipient';
import parsePastedTextForRecipients from '../actions/parsePastedTextForRecipients';
import removeReadWriteRecipients from '../actions/removeReadWriteRecipients';
import resolveAllUnresolvedRecipients from '../actions/resolveAllUnresolvedRecipients';
import resolveQueryStringToRecipient from '../actions/resolveQueryStringToRecipient';
import setCommonWellItemSelectedState from 'owa-readwrite-common-well/lib/actions/setCommonWellItemSelectedState';
import shiftFindResultHighlightIndex from '../actions/shiftFindResultHighlightIndex';
import showHideContactPicker from '../actions/showHideContactPicker';
import showHideFindResult from '../actions/showHideFindResult';
import updateFindControlQueryString from '../actions/updateFindControlQueryString';
import isValidEmailAddress from '../utils/isValidEmailAddress';
import searchDirectory from '../utils/searchDirectory';
import useTypedAddress from '../utils/useTypedAddress';
import { observer } from 'mobx-react';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { FindRecipientInfoType } from 'owa-recipient-common/lib/components/FindRecipientInfo';
import removeFindPeopleEntity from 'owa-recipient-common/lib/utils/removeFindPeopleEntity';
import * as React from 'react';
import RecipientWellWithFindControlViewState, {
    ReadWriteRecipientWellSize,
} from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';

let styles = require('./ReadWriteFindControl.scss');

export interface ReadWriteFindControlProps {
    recipientWell: RecipientWellWithFindControlViewState;
    size: ReadWriteRecipientWellSize;
    className?: string;
    shouldBlockForcedFocus?: boolean;
    onInputFocus?: (evt: React.SyntheticEvent<EventTarget>) => void;
}

@observer
export default class ReadWriteFindControl extends React.Component<ReadWriteFindControlProps, any> {
    private queryInputElement: HTMLElement;
    private inputControl: HTMLInputElement;
    private isForcedFocus: boolean;
    private infoElementType: FindRecipientInfoType;

    public focus() {
        this.isForcedFocus = true;
        this.inputControl.focus();
        this.inputControl.selectionStart = this.props.recipientWell.queryString.length;
    }

    private onChange = (evt: any) => {
        let inputText = evt.target.value;
        updateFindControlQueryString(this.props.recipientWell, inputText);
    };

    private onPaste = (evt: React.ClipboardEvent<EventTarget>) => {
        evt.preventDefault();
        let inputText = evt.clipboardData.getData('Text');
        parsePastedTextForRecipients(this.props.recipientWell, inputText);
    };

    private onFocus = (evt: React.SyntheticEvent<EventTarget>) => {
        if (this.props.shouldBlockForcedFocus && this.isForcedFocus) {
            this.isForcedFocus = false;
        } else if (evt.currentTarget == document.activeElement) {
            showHideFindResult(this.props.recipientWell, true /* shouldShowFindResult */);
        }
        showHideContactPicker(this.props.recipientWell, false /* shouldShowContactPicker */);

        if (this.props.onInputFocus) {
            this.props.onInputFocus(evt);
        }
    };

    private onClick = (evt: React.SyntheticEvent<EventTarget>) => {
        if (!this.props.recipientWell.shouldShowFindResult) {
            showHideFindResult(this.props.recipientWell, true /**shouldShowFindResult */);
        }
    };

    private getQueryInputElement = () => {
        return this.queryInputElement;
    };

    private onKeyDown = (target: React.KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case KeyboardCharCodes.Down_arrow:
                target.preventDefault();
                shiftFindResultHighlightIndex(
                    this.props.recipientWell,
                    true /* isIncrement */,
                    this.infoElementType
                );
                return;
            case KeyboardCharCodes.Up_arrow:
                if (this.props.recipientWell.findResultHightlightIndex > -1) {
                    target.preventDefault();
                    shiftFindResultHighlightIndex(
                        this.props.recipientWell,
                        false /* isIncrement */,
                        this.infoElementType
                    );
                }
                return;
            case KeyboardCharCodes.Left_arrow:
            case KeyboardCharCodes.Right_arrow:
                if (
                    ((target.keyCode == KeyboardCharCodes.Left_arrow && !getRTL()) ||
                        (target.keyCode == KeyboardCharCodes.Right_arrow && getRTL())) &&
                    this.inputControl.selectionStart == 0 &&
                    this.inputControl.selectionEnd == 0 &&
                    this.props.recipientWell.recipients.length > 0
                ) {
                    target.preventDefault();
                    setCommonWellItemSelectedState(
                        this.props.recipientWell.recipients[
                            this.props.recipientWell.recipients.length - 1
                        ],
                        true
                    );
                }
                return;
            case KeyboardCharCodes.Enter:
            case KeyboardCharCodes.Tab:
                if (!target.shiftKey) {
                    if (this.props.recipientWell.findResultHightlightIndex > -1) {
                        target.preventDefault();
                        let resultsLength = this.props.recipientWell.findResultSet.length;
                        if (this.props.recipientWell.findResultHightlightIndex == resultsLength) {
                            if (
                                this.infoElementType === FindRecipientInfoType.TopNResults &&
                                resultsLength == 0
                            ) {
                                target.preventDefault();
                                resolveQueryStringToRecipient(
                                    this.props.recipientWell,
                                    true /* shouldDirectlyResolve */
                                );
                            }
                            if (this.infoElementType === FindRecipientInfoType.SearchDirectory) {
                                searchDirectory(this.props.recipientWell);
                            } else if (this.infoElementType === FindRecipientInfoType.UseAddress) {
                                useTypedAddress(this.props.recipientWell);
                            }
                        } else {
                            addHighlightedFindRecipient(this.props.recipientWell);
                        }
                    } else if (this.props.recipientWell.queryString.length > 0) {
                        // When there are no results, just add the text as a recipient.
                        target.preventDefault();
                        resolveQueryStringToRecipient(
                            this.props.recipientWell,
                            true /* shouldDirectlyResolve */
                        );
                    } else {
                        showHideFindResult(
                            this.props.recipientWell,
                            false /* shouldShowFindResult */
                        );
                    }
                } else {
                    showHideFindResult(this.props.recipientWell, false /**shouldShowFindResult */);
                }
                return;
            case KeyboardCharCodes.K:
                if (target.ctrlKey) {
                    target.preventDefault();
                    resolveAllUnresolvedRecipients(
                        this.props.recipientWell,
                        this.props.recipientWell.recipients
                    );
                }
                return;
            case KeyboardCharCodes.Backspace:
                if (this.inputControl.selectionStart == 0 && this.inputControl.selectionEnd == 0) {
                    removeFindPeopleEntity(
                        this.props.recipientWell.findPeopleFeedbackManager,
                        this.props.recipientWell.recipients[length - 1]
                    );
                    removeReadWriteRecipients(this.props.recipientWell, [
                        this.props.recipientWell.recipients.length - 1,
                    ]);
                    showHideFindResult(this.props.recipientWell, true /**shouldShowFindResult */);
                }
                return;
        }
    };

    private bindQueryInputRef = (ref: HTMLElement) => {
        if (ref) {
            this.queryInputElement = ref;
        }
    };

    render() {
        let size = this.props.size;
        let inputClassName =
            size == ReadWriteRecipientWellSize.Small
                ? styles.findControlInputSmall
                : styles.findControlInputRegular;
        return (
            <span ref={this.bindQueryInputRef} className={this.props.className}>
                <input
                    className={inputClassName}
                    ref={ref => (this.inputControl = ref)}
                    type="text"
                    value={this.props.recipientWell.queryString}
                    onClick={this.onClick}
                    onFocus={this.onFocus}
                    onChange={this.onChange}
                    onPaste={this.onPaste}
                    onKeyDown={this.onKeyDown}
                />
                <FindRecipientResult
                    recipientWell={this.props.recipientWell}
                    queryInputGetter={this.getQueryInputElement}
                    findRecipientInfoType={this.getFindRecipientInfoType()}
                />
            </span>
        );
    }

    private getFindRecipientInfoType = (): FindRecipientInfoType => {
        let { recipientWell } = this.props;
        let hasQueryString = !!recipientWell.queryString;
        if (recipientWell.isSearching) {
            this.infoElementType = FindRecipientInfoType.Searching;
        } else if (!hasQueryString && recipientWell.findResultSet.length == 0) {
            this.infoElementType = FindRecipientInfoType.TypeToSearch;
        } else if (hasQueryString) {
            if (
                recipientWell.findResultSet.length == 0 &&
                recipientWell.queryString.indexOf('@') >= 0 &&
                isValidEmailAddress(recipientWell.queryString)
            ) {
                this.infoElementType = FindRecipientInfoType.UseAddress;
            } else if (!recipientWell.hasSearchedDirectory) {
                this.infoElementType = FindRecipientInfoType.SearchDirectory;
            } else {
                this.infoElementType = FindRecipientInfoType.TopNResults;
            }
        } else {
            this.infoElementType = FindRecipientInfoType.Default;
        }

        return this.infoElementType;
    };
}
